import { z } from "zod"
import {
  branchIdSchema,
  correlationIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const id = z
  .string()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/,
  )
const positive = z.number().int().min(1).max(Number.MAX_SAFE_INTEGER)
const idempotencyKey = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/)
const reason = z.string().regex(/^[a-z][a-z0-9_]{1,63}$/)
const date = z.string().date()

export const purchaseReceiptLineV1Schema = z
  .object({
    purchaseOrderItem: z.string().min(1).max(140),
    itemMasterId: id,
    acceptedLocationId: id,
    rejectedLocationId: id.nullable(),
    acceptedQuantityMilli: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    rejectedQuantityMilli: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    serialBatchBundleId: z.string().min(1).max(140).nullable(),
  })
  .strict()
  .refine(
    (line) =>
      line.acceptedQuantityMilli + line.rejectedQuantityMilli > 0 &&
      (line.rejectedQuantityMilli === 0 || line.rejectedLocationId !== null),
    { message: "Receipt quantity and rejected-location evidence are required." },
  )

const base = {
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey,
  receiptId: id,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  postingDate: date,
  lines: z.array(purchaseReceiptLineV1Schema).min(1).max(100),
}

export const purchaseReceiptPostV1Schema = z
  .object({
    ...base,
    purchaseOrderId: id,
    expectedPurchaseOrderVersion: positive,
    supplierDeliveryNote: z.string().trim().min(1).max(140).nullable(),
  })
  .strict()

export const purchaseReceiptReturnV1Schema = z
  .object({
    ...base,
    originalReceiptId: id,
    reasonCode: reason,
  })
  .strict()

export const purchaseReceiptResultV1Schema = z
  .object({
    entityType: z.literal("purchase_receipt"),
    entityId: id,
    state: z.literal("submitted"),
    version: z.literal(1),
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    purchaseOrderId: id,
    receiptKind: z.enum(["receive", "return"]),
    originalReceiptId: id.nullable(),
    postingDate: date,
    acceptedQuantityMilli: z.number().int().min(0),
    rejectedQuantityMilli: z.number().int().min(0),
    erpnextPurchaseReceipt: z.string().min(1).max(140),
    stockPosted: z.literal(true),
  })
  .strict()

export type PurchaseReceiptPostV1 = z.infer<typeof purchaseReceiptPostV1Schema>
export type PurchaseReceiptReturnV1 = z.infer<typeof purchaseReceiptReturnV1Schema>
export type PurchaseReceiptResultV1 = z.infer<typeof purchaseReceiptResultV1Schema>
